import React, { useState, useRef, useCallback } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../firebase';
import { toast } from 'react-hot-toast';

const PremiumImageUploader = ({ 
  currentImage, 
  onImageChange, 
  uploadPath,
  className = "",
  variant = "default", // "profile", "service", "portfolio"
  label = "Upload Image"
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef(null);

  // Proper sizing configurations
  const variants = {
    profile: {
      container: "w-24 h-24 rounded-full",
      wrapper: "w-24 h-24"
    },
    service: {
      container: "w-28 h-20 rounded-lg",
      wrapper: "w-28 h-20"
    },
    portfolio: {
      container: "w-40 h-28 rounded-lg",
      wrapper: "w-40 h-28"
    },
    default: {
      container: "w-32 h-32 rounded-lg",
      wrapper: "w-32 h-32"
    }
  };

  const config = variants[variant] || variants.default;

  const handleFileUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `${uploadPath}/${auth.currentUser.uid}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      onImageChange(url);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOver(false);
    }
  }, []);

  const removeImage = () => {
    onImageChange('');
    toast.success('Image removed');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div className={config.wrapper}>
        <div
          className={`${config.container} relative overflow-hidden cursor-pointer transition-all duration-200 ${
            dragOver 
              ? 'ring-2 ring-orange-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-900' 
              : 'hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-600 hover:ring-offset-2 hover:ring-offset-white dark:hover:ring-offset-gray-900'
          } ${
            currentImage 
              ? 'bg-gray-100 dark:bg-gray-800' 
              : 'bg-gray-50 dark:bg-gray-900 border-2 border-dashed border-gray-300 dark:border-gray-600'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {currentImage ? (
            <>
              <img
                src={currentImage}
                alt="Upload preview"
                className="w-full h-full object-cover"
              />

              {isHovered && (
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="flex flex-col gap-1 text-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="text-white text-xs font-medium hover:text-gray-200 transition-colors"
                    >
                      Change
                    </button>
                    
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage();
                      }}
                      className="text-red-300 text-xs font-medium hover:text-red-200 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-2 text-center">
              {uploading ? (
                <div className="flex flex-col items-center gap-1">
                  <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs font-medium text-orange-400">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <div className={`${variant === 'profile' ? 'w-6 h-6' : variant === 'service' ? 'w-5 h-5' : 'w-8 h-8'} rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center`}>
                    <svg className={`${variant === 'profile' ? 'w-3 h-3' : variant === 'service' ? 'w-2.5 h-2.5' : 'w-4 h-4'} text-gray-500 dark:text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  
                  <div className="space-y-0.5">
                    <p className={`${variant === 'service' ? 'text-xs' : 'text-sm'} font-medium text-gray-700 dark:text-gray-300`}>
                      {dragOver ? 'Drop here' : 'Click to upload'}
                    </p>
                    {variant !== 'service' && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PNG, JPG up to 10MB
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {uploading && (
            <div className="absolute inset-0 bg-white bg-opacity-80 dark:bg-black dark:bg-opacity-80 flex items-center justify-center">
              <div className="flex flex-col items-center gap-1">
                <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs font-medium text-orange-400">Processing...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      {!currentImage && variant !== 'service' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Drag and drop or click to browse
        </p>
      )}
    </div>
  );
};

export default PremiumImageUploader; 